import { computed, ref } from "vue";
import posthog from "@/posthog";
import { careerCompassService } from "@/modern/services/careerCompassService";
import {
  CareerCompassAnswers,
  CareerCompassQuiz,
  CareerCompassResult,
  CareerCompassStep,
} from "@/modern/types/careerCompass";

type Mode = "loading" | "error" | "intro" | "quiz" | "result";

/**
 * Estado da bússola de carreira. O cálculo do roteiro é da API; aqui só se
 * coletam as respostas e se guarda o último resultado devolvido.
 */
export function useCareerCompass() {
  const quiz = ref<CareerCompassQuiz | null>(null);
  const result = ref<CareerCompassResult | null>(null);
  const mode = ref<Mode>("loading");

  const answers = ref<CareerCompassAnswers>({});
  const stepIndex = ref(0);
  const isSubmitting = ref(false);
  const submitError = ref(false);

  const profile = computed(() =>
    quiz.value?.profiles.find((candidate) => candidate.id === answers.value.profile) ?? null
  );

  // Perfil e destino primeiro; as perguntas de nível dependem do perfil escolhido.
  const steps = computed<CareerCompassStep[]>(() => {
    if (!quiz.value) return [];

    const list: CareerCompassStep[] = [
      { id: "profile", kind: "profile", options: quiz.value.profiles.map((candidate) => candidate.id) },
    ];

    if (profile.value) {
      list.push({ id: "goal", kind: "goal", options: profile.value.goals });
      profile.value.level_questions.forEach((question) =>
        list.push({ id: question, kind: "level", options: quiz.value!.level_options })
      );
    }

    return list;
  });

  // Antes de escolher o perfil só existe 1 passo; o total real usa as
  // perguntas do primeiro perfil, que todos os perfis têm na mesma quantidade.
  const totalSteps = computed(() =>
    profile.value ? steps.value.length : 2 + (quiz.value?.profiles[0]?.level_questions.length ?? 0)
  );

  const currentStep = computed(() => steps.value[stepIndex.value] ?? null);
  const isLastStep = computed(() => profile.value !== null && stepIndex.value === steps.value.length - 1);
  const canSubmit = computed(() => steps.value.every((step) => Boolean(answers.value[step.id])));

  async function load(): Promise<void> {
    mode.value = "loading";

    try {
      const payload = await careerCompassService.fetch();
      quiz.value = payload.quiz;
      result.value = payload.result;
      mode.value = payload.result ? "result" : "intro";
    } catch (e) {
      mode.value = "error";
    }
  }

  function start(): void {
    answers.value = {};
    stepIndex.value = 0;
    submitError.value = false;
    mode.value = "quiz";
  }

  function select(option: string): void {
    const step = currentStep.value;
    if (!step) return;

    // Trocar de perfil invalida destino e perguntas de nível do perfil anterior.
    if (step.kind === "profile" && answers.value.profile !== option) {
      answers.value = {};
    }
    answers.value = { ...answers.value, [step.id]: option };

    if (!isLastStep.value) {
      stepIndex.value += 1;
    }
  }

  function back(): void {
    if (stepIndex.value > 0) {
      stepIndex.value -= 1;
    } else {
      mode.value = result.value ? "result" : "intro";
    }
  }

  async function submit(): Promise<void> {
    if (!canSubmit.value || isSubmitting.value) return;

    isSubmitting.value = true;
    submitError.value = false;

    try {
      const payload = await careerCompassService.submit(answers.value);
      quiz.value = payload.quiz;
      result.value = payload.result;
      mode.value = "result";

      if (payload.result) {
        posthog.capture("career_compass_completed", {
          profile: payload.result.profile,
          goal: payload.result.goal,
          level: payload.result.level,
          score: payload.result.score,
        });
      }
    } catch (e) {
      submitError.value = true;
    } finally {
      isSubmitting.value = false;
    }
  }

  return {
    quiz,
    result,
    mode,
    answers,
    stepIndex,
    steps,
    totalSteps,
    currentStep,
    isLastStep,
    canSubmit,
    isSubmitting,
    submitError,
    load,
    start,
    select,
    back,
    submit,
  };
}
